import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { CreativeLightbox } from '@/components/chat/CreativeLightbox'
import { CreativeMedia } from '@/components/chat/CreativeMedia'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { renderPdfFirstPagePreview } from '@/lib/utils/extract-pdf-text'
import type {
  BillingPeriod,
  ConceptAsset,
  QuestionnaireValidationIssue,
  TestedAssetType,
} from '@digital-twins/shared-types'
import {
  assetContentSupplied,
  assetLink,
  clampToMaxWords,
  DEFAULT_MAX_ASSET_NAME_LENGTH,
  DESCRIPTION_REQUIRED_FAMILIES,
  EXPOSURE_BOUND_FAMILIES,
  FILE_CARRYING_FAMILIES,
  isAssetFamily,
  BILLING_PERIODS,
  NOT_THE_PAGE_CAPTURE_CODES,
  PRODUCT_PRICE_CURRENCIES,
  productVersionLabel,
  respondentWordingFor,
  savesCaptureOfLink,
  TESTED_ASSET_MAX_BY_TYPE,
} from '@digital-twins/shared-types'
import { Check, ChevronDown, FileText, ImagePlus, Link2, Loader2, Plus, Trash2, Upload, X, ZoomIn, ZoomOut, PlayFilled } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'

import { ASSET_MEDIA_ACCEPT } from '../assets/assetMediaAccept'
import {
  assetMediaUrl,
  isImageMedia,
  isVideoMedia,
  materializedPreviewMedia,
  primaryAssetMedia,
  primaryMediaUrl,
} from '../assets/assetMedia'
import {
  ASSET_TYPE_BADGE,
  assetPanelLabel,
  assetTextReachesRespondents,
  assetTypeCopy,
  patchFromMessageBody,
  patchFromMessageTitle,
  patchFromProductPrice,
  productPriceOf,
  withAssetTextProvenance,
} from '../assets/conceptAssets'
import { formatIssueCopy } from '../utils/questionnaireIssueCopy'
import { UrlCaptureDialog } from '@/components/media/UrlCaptureDialog'
import { refusedCaptureError } from '@/components/media/useUrlCaptures'
import { captureQuestionnaireUrlApi } from '../api/questionnaireDraftsApi'
import type { IssuedAssetFile } from '@/lib/api/client'
const ADDABLE_ASSET_TYPES = Object.keys(ASSET_TYPE_BADGE) as Array<TestedAssetType>

function capitalizeFirst(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

// Mirrors assetTypeDisplayName in conceptAssets.ts — used to detect/regenerate a
// concept's auto-generated name ("Concept 1") when the type changes.
function assetTypeDisplayName(type: TestedAssetType) {
  const spaced = String(type).replace(/_/g, ' ')
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`
}

/**
 * Section-header asset-type selector — the panel title ("Concepts") is itself a
 * dropdown (like the question TypePicker) that sets the asset type for the panel.
 * No icon; rendered as a plain sans button (not an <h3>, which the global
 * heading rule would force into the serif display font).
 */
function AssetTypeHeaderPicker(props: {
  title: string
  currentType: TestedAssetType | null
  autoOpen: boolean
  onDidAutoOpen: () => void
  onChange: (type: TestedAssetType) => void
  /** True while a chat turn is in flight; the picker becomes read-only. */
  disabled?: boolean
  /** Copy for `title` on the disabled trigger/options. */
  disabledReason?: string
}) {
  const { title, currentType, autoOpen, onDidAutoOpen, onChange, disabled = false, disabledReason } = props
  const { t } = useTranslation('app')
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true)
      onDidAutoOpen()
    }
  }, [autoOpen, onDidAutoOpen])

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        data-testid="questionnaire-assets-type-button"
        className="-mx-1.5 inline-flex items-center gap-1 whitespace-nowrap rounded-[6px] border border-transparent px-1.5 py-1 font-sans text-[12px] font-medium leading-none text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground/70 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {title}
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
      </button>
      {isOpen ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div
            className="absolute left-0 top-7 z-30 w-52 rounded-[8px] border border-border bg-popover p-1 shadow-md"
            data-testid="questionnaire-assets-type-menu"
          >
            {ADDABLE_ASSET_TYPES.map((assetType) => (
              <button
                key={assetType}
                type="button"
                onClick={() => {
                  onChange(assetType)
                  setIsOpen(false)
                }}
                disabled={disabled}
                title={disabled ? disabledReason : undefined}
                data-testid={`questionnaire-assets-type-option-${assetType}`}
                className="flex w-full items-center justify-between gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-[12.5px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span>
                  {capitalizeFirst(assetTypeCopy(t, assetType).singular)}
                </span>
                {assetType === currentType ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </span>
  )
}

/** The DOM id of an asset's card in this panel: the one place it is named. */
function assetCardElementId(assetId: string): string {
  return `questionnaire-asset-card-${assetId}`
}

/**
 * Takes the user to an asset's card, from wherever a question points at it
 * (a locked answer renamed through its asset, C23-e). Focus lands on the
 * card, which opens a folded panel (the body opens on focus within).
 */
export function revealAssetCard(assetId: string): void {
  const card = document.getElementById(assetCardElementId(assetId))
  if (!card) return
  card.focus({ preventScroll: true })
  card.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
}

interface ConceptAssetsPanelProps {
  concepts: Array<ConceptAsset>
  /** Types declared during study setup — may be non-empty with zero assets. */
  declaredAssetTypes?: Array<TestedAssetType>
  /** True when the study design requires assets (LP/ad goals) — hides the Optional badge. */
  assetsRequired?: boolean
  onAddConcept: (type?: TestedAssetType) => void
  onRemoveConcept: (conceptId: string) => void
  onUpdateConcept: (conceptId: string, patch: Partial<ConceptAsset>) => void
  /** Header type-switch: replace the block's declared assetTypes. */
  onChangeDeclaredAssetTypes?: (types: Array<TestedAssetType>) => void
  onUploadConceptImage: (conceptId: string, file: File) => Promise<void>
  /** A page capture the backend stored as an asset file. */
  onAttachConceptCapture: (conceptId: string, file: IssuedAssetFile, url: string) => void
  onAttachConceptUrl?: (conceptId: string, url: string) => Promise<void>
  /**
   * Concepts whose new video the Save in flight is watching. The read takes up
   * to ~90s and fills the name and description boxes when it lands, so the card
   * says so rather than looking like an empty form.
   */
  analyzingConceptIds?: Set<string>
  /**
   * What the server says about each asset, keyed by its id: the saved draft's
   * readiness verdict, blocking and advice, the one verdict on what a card
   * still needs (QA4 FE3, N3); while edits are unsaved, the last refused
   * Save's refusals of an edit to it (QA4 N7). A refused price marks the
   * price field.
   */
  issuesByAssetId?: Map<string, Array<QuestionnaireValidationIssue>>
  /**
   * True while a chat turn is in flight. The landing draft replaces this one,
   * so an edit made now would be reverted with no explanation.
   */
  disabled?: boolean
  /** Copy for `title` on the disabled controls. */
  disabledReason?: string
  /** The draft's content language: a price's period reads in it, as respondents read it. */
  contentLanguage?: string
  /**
   * The set's shared context, the user's words every item is shown with
   * (shared-types `readTestedAssetSharedContext`, the reader the compiler puts
   * on the twins' stimulus). Shown read-only; it changes through the chat.
   */
  sharedContext?: string
}

const NO_CONCEPTS_ANALYZING: Set<string> = new Set()

// The editor's label for each billing period (`BILLING_PERIODS`).
const PRICE_PERIOD_LABEL_KEYS = {
  month: 'questionnaire.pricePeriodMonth',
  year: 'questionnaire.pricePeriodYear',
} as const satisfies Record<BillingPeriod, string>

function isPdfMedia(mimeType?: string) {
  return mimeType === 'application/pdf'
}

/**
 * The server's verdict that an asset's link shows another page, a block or a
 * blank (its readiness line, one of `NOT_THE_PAGE_CAPTURE_CODES`), with the
 * link it was given: the Save drops any picture of that link (H08-LP).
 */
function notThePageVerdict(
  concept: ConceptAsset | undefined,
  issues: ReadonlyArray<QuestionnaireValidationIssue>,
): { url: string; line: string } | null {
  const verdict = issues.find((issue) => NOT_THE_PAGE_CAPTURE_CODES.includes(issue.code as never))
  // The page's link as the Save reads it (`assetLink`): its bare link once the
  // read dropped the picture, else the page's own.
  const url = assetLink(concept)
  return verdict && url ? { url, line: formatIssueCopy(verdict).text } : null
}

export function ConceptAssetsPanel(props: ConceptAssetsPanelProps) {
  const {
    concepts,
    declaredAssetTypes = [],
    assetsRequired = false,
    onAddConcept,
    onRemoveConcept,
    onUpdateConcept,
    onChangeDeclaredAssetTypes,
    onUploadConceptImage,
    onAttachConceptCapture,
    analyzingConceptIds = NO_CONCEPTS_ANALYZING,
    issuesByAssetId,
    onAttachConceptUrl,
    disabled = false,
    disabledReason,
    contentLanguage,
    sharedContext,
  } = props
  const { t } = useTranslation('app')
  // The panel can mount twice (inline and in the narrow-width drawer), so a
  // label's `for` is scoped to this mount.
  const fieldIdPrefix = useId()
  // Auto-open the header type picker after "Add an asset" so the user picks the
  // type right away — like adding a question. Set on click; the header picker
  // consumes it when it mounts.
  const [autoOpenHeader, setAutoOpenHeader] = useState(false)
  // Same disclosure as StudyObjectivesRow: local only, starts open so existing
  // assets stay visible until the marketer folds the card away.
  const [expanded, setExpanded] = useState(true)
  const [uploadingConceptId, setUploadingConceptId] = useState<string | null>(null)
  // Which concept's "add by URL" capture dialog is open (null = none).
  const [captureConceptId, setCaptureConceptId] = useState<string | null>(null)
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null)
  const [zoom, setZoom] = useState(1)
  // Video click-to-expand goes through CreativeLightbox (D1), NOT the image zoom
  // overlay above: the lightbox has no zoom, so images must keep their own overlay.
  const [videoPreview, setVideoPreview] = useState<{ src: string; mimeType?: string } | null>(null)
  // Concepts whose video read has FINISHED. The framing line then takes the
  // spinner's slot: the boxes above were filled in FOR the marketer, and this is
  // the line that says whose words the twins actually hear.
  const [pdfPreviewByMediaId, setPdfPreviewByMediaId] = useState<Record<string, string>>({})
  const pdfRenderingRef = useRef<Set<string>>(new Set())
  const label = assetPanelLabel(concepts, declaredAssetTypes, t)
  const priceWording = respondentWordingFor(contentLanguage)
  // With exactly one known type the first-asset button adds it directly; with
  // none, "Add an asset" adds a default card and auto-opens its type picker.
  const knownFirstAssetType =
    declaredAssetTypes.length === 1 ? declaredAssetTypes[0] : undefined

  const collapsedSummary =
    concepts.length === 1
      ? concepts[0].name.trim()
        ? productVersionLabel(concepts[0].name.trim(), productPriceOf(concepts[0]), priceWording)
        : `1 ${label.singular}`
      : `${concepts.length} ${label.plural.toLowerCase()}`

  // "Add an asset" (empty state): add a default card and flag the header type
  // picker to auto-open once it mounts.
  const addConceptAndOpenType = useCallback(() => {
    setExpanded(true)
    setAutoOpenHeader(true)
    onAddConcept()
  }, [onAddConcept])

  function addConceptExpanded(type?: TestedAssetType) {
    setExpanded(true)
    if (type === undefined) {
      onAddConcept()
    } else {
      onAddConcept(type)
    }
  }

  // The panel's asset type: the shared type when all concepts agree, else null.
  const conceptTypes = new Set(concepts.map((c) => c.type as TestedAssetType))
  const currentPanelType = conceptTypes.size === 1 ? [...conceptTypes][0] : null

  // Cap check uses the full roster (held cards count too). Raise-only is a
  // backend rule; the panel only refuses adding another once at the type max.
  const cappedType = currentPanelType ?? knownFirstAssetType
  const typeCap =
    cappedType !== undefined ? TESTED_ASSET_MAX_BY_TYPE[cappedType] : undefined
  const typeCount =
    cappedType !== undefined
      ? concepts.filter((concept) => concept.type === cappedType).length
      : 0
  const isAtTypeCap = typeCap !== undefined && typeCount >= typeCap
  const typeCapReason =
    isAtTypeCap && cappedType !== undefined && typeCap !== undefined
      ? t('questionnaire.typeCapReached', {
          count: typeCap,
          type: assetTypeCopy(t, cappedType).plural.toLowerCase(),
        })
      : undefined
  const addDisabled = disabled || isAtTypeCap
  const addDisabledReason = disabled
    ? disabledReason
    : isAtTypeCap
      ? typeCapReason
      : undefined

  // Switching the header type re-types every asset in the panel (single-type
  // panels), regenerating any still-default auto-name so titles track the type.
  const changeAllConceptsType = useCallback(
    (nextType: TestedAssetType) => {
      concepts.forEach((concept, index) => {
        const patch: Partial<ConceptAsset> & Record<string, unknown> = {
          type: nextType as ConceptAsset['type'],
        }
        const autoName = `${assetTypeDisplayName(concept.type as TestedAssetType)} ${index + 1}`
        if (!concept.name.trim() || concept.name.trim() === autoName) {
          patch.name = `${assetTypeDisplayName(nextType)} ${index + 1}`
        }
        // Manage the per-type REQUIRED fields across the switch (platform
        // templates), and drop the ones the new type doesn't have — otherwise
        // a re-typed message/packaging asset fails asset_required_field_missing
        // at review, or carries stale fields forever.
        const record = concept as unknown as Record<string, unknown>
        if (nextType === 'message') {
          const existingText =
            typeof record.messageText === 'string' ? record.messageText.trim() : ''
          if (existingText) {
            patch.messageText = existingText
          } else {
            // Blank, with the card's placeholder (QA4 FE3).
            patch.messageText = ''
            patch.metadata = withAssetTextProvenance(concept.metadata, {
              ...(typeof patch.name === 'string' ? { name: 'placeholder' as const } : {}),
              description: 'placeholder',
            })
          }
        } else {
          patch.messageText = undefined
        }
        onUpdateConcept(concept.id, patch)
      })
      // The block-level half of the switch: the declared assetTypes are
      // REPLACED, not accumulated. The derive path only unions, so without
      // this a pass through a text-collect type (product, message, pain,
      // concept) stays declared with zero matching cards and the send guard
      // refuses every turn from then on.
      onChangeDeclaredAssetTypes?.([nextType])
    },
    [concepts, onChangeDeclaredAssetTypes, onUpdateConcept],
  )

  useEffect(() => {
    concepts.forEach((concept) => {
      const media = primaryAssetMedia(concept)
      const mediaUrl = primaryMediaUrl(concept)
      const materializedMedia = materializedPreviewMedia(concept, media?.id)
      if (materializedMedia) return
      if (!media?.id || !mediaUrl || !isPdfMedia(media.mimeType)) return
      if (pdfRenderingRef.current.has(media.id)) return
      pdfRenderingRef.current.add(media.id)
      const mediaId = media.id
      void renderPdfFirstPagePreview(mediaUrl, 240)
        .then((previewUrl) => {
          setPdfPreviewByMediaId((current) => ({
            ...current,
            [mediaId]: previewUrl,
          }))
        })
        .catch(() => {
          pdfRenderingRef.current.delete(mediaId)
        })
    })
  }, [concepts])

  const ZOOM_MIN = 1
  const ZOOM_MAX = 4
  const ZOOM_STEP = 0.5

  const closePreview = useCallback(() => {
    setPreviewImage(null)
    setZoom(1)
  }, [])

  const zoomIn = useCallback(() => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 10) / 10)), [])
  const zoomOut = useCallback(() => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 10) / 10)), [])

  useEffect(() => {
    if (!previewImage) return
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closePreview()
      else if (event.key === '+' || event.key === '=') zoomIn()
      else if (event.key === '-' || event.key === '_') zoomOut()
      else if (event.key === '0') setZoom(1)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [previewImage, closePreview, zoomIn, zoomOut])

  return (
    <>
    {previewImage && (
      <div
        className={`fixed inset-0 z-50 flex justify-center overflow-auto bg-black/60 p-6 backdrop-blur-sm ${
          zoom > 1 ? 'items-start' : 'items-center'
        }`}
        onClick={closePreview}
        data-testid="concept-image-preview-overlay"
      >
        <button
          type="button"
          className="fixed right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          onClick={closePreview}
          aria-label={t("questionnaire.closePreview")}
          data-testid="concept-image-preview-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div
          className="fixed bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/70 px-2 py-1.5 text-white shadow-lg backdrop-blur-sm"
          onClick={(event) => event.stopPropagation()}
          data-testid="concept-image-preview-zoom-controls"
        >
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/15 disabled:opacity-40"
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
            aria-label={t("questionnaire.zoomOut")}
            data-testid="concept-image-preview-zoom-out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="min-w-[3.25rem] rounded-full px-2 text-[12px] font-medium tabular-nums transition-colors hover:bg-white/15"
            onClick={() => setZoom(1)}
            aria-label={t("questionnaire.resetZoom")}
            data-testid="concept-image-preview-zoom-reset"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/15 disabled:opacity-40"
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
            aria-label={t("questionnaire.zoomIn")}
            data-testid="concept-image-preview-zoom-in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
        <img
          src={previewImage.src}
          alt={previewImage.alt}
          className={`max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl transition-transform duration-150 ${
            zoom >= ZOOM_MAX ? 'cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
          onClick={(event) => {
            event.stopPropagation()
            if (zoom >= ZOOM_MAX) setZoom(1)
            else zoomIn()
          }}
          data-testid="concept-image-preview-img"
        />
      </div>
    )}
    {videoPreview && (
      <CreativeLightbox
        creatives={[{ src: videoPreview.src, mimeType: videoPreview.mimeType }]}
        index={0}
        onIndex={() => {}}
        onClose={() => setVideoPreview(null)}
      />
    )}
    {concepts.length === 0 ? (
      <div
        className="relative mb-3.5"
        data-testid="questionnaire-concept-assets-panel"
      >
        {knownFirstAssetType ? (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-border bg-transparent p-4 text-[13.5px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onAddConcept(knownFirstAssetType)}
            disabled={addDisabled}
            title={addDisabledReason}
            data-testid="questionnaire-add-first-concept"
          >
            <ImagePlus className="h-4 w-4" />
            {t('questionnaire.addFirst', { type: label.singular })}
            {!assetsRequired ? (
              <span
                className="font-normal text-muted-foreground/70"
                data-testid="questionnaire-assets-optional-badge"
              >
                {t('questionnaire.optional')}
              </span>
            ) : null}
          </button>
        ) : (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-border bg-transparent p-4 text-[13.5px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => addConceptAndOpenType()}
            disabled={addDisabled}
            title={addDisabledReason}
            data-testid="questionnaire-add-asset"
          >
            <ImagePlus className="h-4 w-4" />
            {t('questionnaire.addAsset')}
            <span className="font-normal text-muted-foreground/70">{t('questionnaire.addAssetHint')}</span>
            {!assetsRequired ? (
              <span
                className="font-normal text-muted-foreground/70"
                data-testid="questionnaire-assets-optional-badge"
              >
                {t('questionnaire.optional')}
              </span>
            ) : null}
          </button>
        )}
      </div>
    ) : (
      <section
        className="group mb-3.5 overflow-hidden rounded-[12px] border border-border/60 bg-white dark:bg-card"
        data-testid="questionnaire-concept-assets-panel"
      >
        {/* Header mirrors StudyObjectivesRow: type picker stays a sibling of the
            disclosure toggle so we never nest buttons. */}
        <div className="flex items-center gap-2 px-4 py-2.5 transition-smooth hover:bg-muted/40 dark:hover:bg-white/[0.03]">
          {expanded ? (
            <AssetTypeHeaderPicker
              title={label.plural}
              currentType={currentPanelType}
              autoOpen={autoOpenHeader}
              onDidAutoOpen={() => setAutoOpenHeader(false)}
              onChange={changeAllConceptsType}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          ) : null}
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            data-testid="questionnaire-assets-toggle"
          >
            {!expanded ? (
              <>
                <Text
                  as="span"
                  className="whitespace-nowrap text-[12px] font-medium leading-none text-muted-foreground"
                >
                  {label.plural}
                </Text>
                <span className="text-muted-foreground/60" aria-hidden="true">
                  ·
                </span>
                <Text
                  as="span"
                  className="min-w-0 flex-1 truncate text-[13px] leading-none text-foreground"
                  data-testid="questionnaire-assets-summary"
                >
                  {collapsedSummary}
                </Text>
              </>
            ) : null}
            <ChevronDown
              className={cn(
                'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-smooth',
                expanded && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
        </div>

        <div
          className={cn(
            'grid overflow-hidden motion-reduce:transition-none',
            'transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]',
            expanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'pointer-events-none grid-rows-[0fr] opacity-0',
          )}
          aria-hidden={!expanded}
          data-testid="questionnaire-assets-body"
          // Focus inside a folded body opens it: a card reached from a
          // question (`revealAssetCard`) or by keyboard is never left hidden.
          onFocus={() => setExpanded(true)}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="px-5 pb-4 pt-0.5">
            <Text size="xs" color="muted" className="mb-3">
              {concepts.every((c) => (c.type as string) === 'pain')
                ? t('questionnaire.mentionWithHandle', { handle: label.singular })
                : t('questionnaire.mentionWithHandleAndFiles', { handle: label.singular })}
            </Text>
            {sharedContext ? (
              <Text
                size="xs"
                className="mb-3 whitespace-pre-line rounded-[8px] bg-muted/50 px-3 py-2 text-foreground"
                data-testid="questionnaire-assets-shared-context"
              >
                <span className="font-medium">{t('questionnaire.assetsSharedContext')}</span>{' '}
                {sharedContext}
              </Text>
            ) : null}
            <div className="flex flex-col divide-y divide-border">
              {concepts.map((concept) => {
                const conceptIndex = concepts.findIndex(
                  (candidate) => candidate.id === concept.id,
                )
                const media = primaryAssetMedia(concept)
                const mediaUrl = primaryMediaUrl(concept)
                const materializedMedia = materializedPreviewMedia(concept, media?.id)
                const materializedMediaUrl = assetMediaUrl(materializedMedia)
                const hasImagePreview = Boolean(mediaUrl && isImageMedia(media?.mimeType))
                const pdfPreviewUrl = media?.id ? pdfPreviewByMediaId[media.id] : undefined
                const previewUrl = materializedMediaUrl || (hasImagePreview ? mediaUrl : pdfPreviewUrl)
                const isUploading = uploadingConceptId === concept.id
                // What this card still needs is the server's verdict on the
                // saved draft (readiness), never a client rule (QA4 FE3). A
                // field is marked when an issue's path ends at it.
                const assetIssues = issuesByAssetId?.get(concept.id) ?? []
                const issueAtField = (...fields: Array<string>) =>
                  assetIssues.some((issue) =>
                    fields.includes(issue.path.slice(issue.path.lastIndexOf('.') + 1)),
                  )
                const isNameMissing = issueAtField('name')
                const isMessageAsset = (concept.type as string) === 'message'
                const messageBodyText = isMessageAsset
                  ? String((concept as unknown as { messageText?: string }).messageText ?? '')
                  : ''
                const isDescriptionMissing = issueAtField('description', 'messageText')
                // The field is marked required for the registry's families
                // defined by their text, unless respondents are shown the
                // item's file: the rule validate reads (`assetContentSupplied`,
                // QA4 AD N4; a concept that is its board needs no caption);
                // a message's text is its own field.
                const isDescriptionRequired =
                  isMessageAsset ||
                  (isAssetFamily(concept.type) &&
                    DESCRIPTION_REQUIRED_FAMILIES.has(concept.type) &&
                    !assetContentSupplied(concept, false))
                // The server's verdict only (readiness, a refused Save or the
                // dry run of this Save, T1b): no client rule.
                const validationIssues = [
                  ...new Set(
                    assetIssues
                      .filter((issue) => issue.severity === 'error')
                      .map((issue) => formatIssueCopy(issue).text),
                  ),
                ]
                const hasIssues = validationIssues.length > 0
                // The verdict's advice on this item: guidance, never a block.
                const advice = assetIssues.filter((issue) => issue.severity !== 'error')
                const isAnalyzing = analyzingConceptIds.has(concept.id)
                // A product's price is what a price version is: the card shows it
                // beside the name, "name · price", as the builder and Review read it.
                const isProductAsset = (concept.type as string) === 'product'
                const price = productPriceOf(concept)
                // A price the server refused (a Save's refusal on it, its dry
                // run, or the verdict) marks the field.
                const isPriceInvalid =
                  assetIssues.some(
                    (issue) => issue.severity === 'error' && issue.path.endsWith('.price'),
                  )
                // The currencies the shared formatter spells as the report does,
                // and a stored code outside them, so the field shows what is saved.
                const currencies =
                  price?.currencyCode &&
                  !(PRODUCT_PRICE_CURRENCIES as ReadonlyArray<string>).includes(price.currencyCode)
                    ? [price.currencyCode, ...PRODUCT_PRICE_CURRENCIES]
                    : PRODUCT_PRICE_CURRENCIES
                const conceptSingular =
                  ASSET_TYPE_BADGE[concept.type as TestedAssetType]
                    ? assetTypeCopy(t, concept.type as TestedAssetType).singular
                    : label.singular
                return (
                  <div
                    key={concept.id}
                    id={assetCardElementId(concept.id)}
                    tabIndex={-1}
                    className="py-4 outline-none first:pt-0 last:pb-0"
                    data-testid={`questionnaire-concept-card-${concept.id}`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span className="shrink-0 rounded-[5px] bg-[#EEE4FF] px-2 py-1 text-[11px] font-semibold text-[#7C3AED]">
                        {ASSET_TYPE_BADGE[concept.type as TestedAssetType] ?? label.badge}
                        {conceptIndex + 1}
                      </span>
                      <Input
                        value={concept.name}
                        required
                        aria-invalid={isNameMissing}
                        maxLength={DEFAULT_MAX_ASSET_NAME_LENGTH}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          // A typed name is the marketer's. Recording that is what
                          // stops a later automatic read from overwriting it.
                          // Message assets: Title is a label only (D4), so it
                          // writes `name` and never touches messageText.
                          if (isMessageAsset) {
                            onUpdateConcept(
                              concept.id,
                              patchFromMessageTitle(event.target.value, concept.metadata),
                            )
                            return
                          }
                          onUpdateConcept(concept.id, {
                            name: event.target.value,
                            metadata: withAssetTextProvenance(concept.metadata, {
                              name: 'human',
                            }),
                          })
                        }}
                        placeholder={t("questionnaire.assetTitle")}
                        disabled={disabled}
                        title={disabled ? disabledReason : undefined}
                        className={`h-8 flex-1 rounded-none border-0 border-b bg-transparent px-0 text-[13px] font-medium shadow-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 ${
                          isNameMissing ? 'border-[#E5484D]' : 'border-border'
                        }`}
                        data-testid={`questionnaire-concept-title-${concept.id}`}
                      />
                      {isProductAsset ? (
                        <>
                          <span className="shrink-0 text-muted-foreground/60" aria-hidden="true">
                            ·
                          </span>
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="any"
                            value={price !== undefined && Number.isFinite(price.amount) ? price.amount : ''}
                            aria-invalid={isPriceInvalid}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              onUpdateConcept(
                                concept.id,
                                patchFromProductPrice(
                                  event.currentTarget.valueAsNumber,
                                  price?.currencyCode ?? '',
                                  price?.billingPeriod,
                                ),
                              )
                            }}
                            placeholder={t('questionnaire.priceAmount')}
                            aria-label={t('questionnaire.priceAmount')}
                            disabled={disabled}
                            title={disabled ? disabledReason : undefined}
                            className={`h-8 w-20 shrink-0 rounded-none border-0 border-b bg-transparent px-0 text-[13px] font-medium tabular-nums shadow-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 ${
                              isPriceInvalid ? 'border-[#E5484D]' : 'border-border'
                            }`}
                            data-testid={`questionnaire-concept-price-amount-${concept.id}`}
                          />
                          <select
                            value={price?.currencyCode ?? ''}
                            aria-invalid={isPriceInvalid}
                            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                              onUpdateConcept(
                                concept.id,
                                patchFromProductPrice(
                                  price?.amount ?? Number.NaN,
                                  event.target.value,
                                  price?.billingPeriod,
                                ),
                              )
                            }}
                            aria-label={t('questionnaire.priceCurrency')}
                            disabled={disabled}
                            title={disabled ? disabledReason : undefined}
                            className={`h-8 w-16 shrink-0 rounded-none border-0 border-b bg-transparent px-0 text-[13px] font-medium outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                              isPriceInvalid ? 'border-[#E5484D]' : 'border-border'
                            }`}
                            data-testid={`questionnaire-concept-price-currency-${concept.id}`}
                          >
                            <option value="">{t('questionnaire.priceCurrency')}</option>
                            {currencies.map((code) => (
                              <option key={code} value={code}>
                                {code}
                              </option>
                            ))}
                          </select>
                          <select
                            value={price?.billingPeriod ?? ''}
                            aria-invalid={isPriceInvalid}
                            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                              onUpdateConcept(
                                concept.id,
                                patchFromProductPrice(
                                  price?.amount ?? Number.NaN,
                                  price?.currencyCode ?? '',
                                  (event.target.value || undefined) as BillingPeriod | undefined,
                                ),
                              )
                            }}
                            aria-label={t('questionnaire.pricePeriod')}
                            disabled={disabled}
                            title={disabled ? disabledReason : undefined}
                            className={`h-8 w-24 shrink-0 rounded-none border-0 border-b bg-transparent px-0 text-[13px] font-medium outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                              isPriceInvalid ? 'border-[#E5484D]' : 'border-border'
                            }`}
                            data-testid={`questionnaire-concept-price-period-${concept.id}`}
                          >
                            <option value="">{t('questionnaire.pricePeriodNone')}</option>
                            {BILLING_PERIODS.map((period) => (
                              <option key={period} value={period}>
                                {t(PRICE_PERIOD_LABEL_KEYS[period])}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : null}
                      <button
                        type="button"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted-foreground transition-colors hover:bg-background hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => onRemoveConcept(concept.id)}
                        disabled={disabled}
                        title={disabled ? disabledReason : undefined}
                        aria-label={t('questionnaire.removeAsset', { type: conceptSingular })}
                        data-testid={`questionnaire-remove-concept-${concept.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label
                        htmlFor={`${fieldIdPrefix}-description-${concept.id}`}
                        className="text-[11px] font-medium text-muted-foreground"
                      >
                        {isMessageAsset
                          ? t('questionnaire.messageTextLabel')
                          : t('questionnaire.descriptionLabel')}
                        {isDescriptionRequired ? (
                          <>
                            {' '}
                            <span aria-hidden className="text-[#E5484D]">*</span>
                          </>
                        ) : null}
                      </label>
                      {isDescriptionMissing ? (
                        <span className="text-[11px] font-medium text-[#E5484D]">
                          Required
                        </span>
                      ) : !isMessageAsset &&
                        concept.description?.trim() &&
                        !assetTextReachesRespondents(concept, 'description') ? (
                        // A placeholder is the author's context only; the
                        // marketer must not think respondents see it.
                        <span
                          className="text-[11px] font-medium text-muted-foreground"
                          data-testid={`questionnaire-description-not-shown-${concept.id}`}
                        >
                          {t('questionnaire.descriptionNotShownToRespondents')}
                        </span>
                      ) : null}
                    </div>
                    <Textarea
                      id={`${fieldIdPrefix}-description-${concept.id}`}
                      value={isMessageAsset ? messageBodyText : concept.description || ''}
                      required={isDescriptionRequired}
                      aria-invalid={isDescriptionMissing}
                      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                        if (isMessageAsset) {
                          onUpdateConcept(
                            concept.id,
                            patchFromMessageBody(clampToMaxWords(event.target.value), concept.metadata),
                          )
                          return
                        }
                        onUpdateConcept(concept.id, {
                          description: clampToMaxWords(event.target.value),
                          metadata: withAssetTextProvenance(concept.metadata, {
                            description: 'human',
                          }),
                        })
                      }}
                      rows={3}
                      disabled={disabled}
                      title={disabled ? disabledReason : undefined}
                      className={`min-h-[86px] resize-none rounded-[7px] bg-background p-2.5 text-[12.5px] leading-[1.45] dark:bg-card disabled:cursor-not-allowed disabled:opacity-50 ${
                        isDescriptionMissing ? 'border-[#E5484D]' : 'border-border'
                      }`}
                      // "Required" only where validate requires it: a pain's
                      // description is valid empty (registry requiredFields).
                      placeholder={
                        isMessageAsset
                          ? t('questionnaire.messageTextPlaceholder')
                          : isDescriptionRequired
                            ? t('questionnaire.describeRequired')
                            : undefined
                      }
                      data-testid={`questionnaire-concept-description-${concept.id}`}
                    />
                    {isAnalyzing ? (
                      <div
                        className="mt-1.5 flex items-center gap-2"
                        data-testid={`questionnaire-concept-reading-${concept.id}`}
                      >
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                        <span className="text-[11px] text-muted-foreground">
                          {t('questionnaire.readingAsset', { type: conceptSingular })}
                        </span>
                      </div>
                    ) : null}
                    {/* The registry's `carriesFile`: which families take a picture or video. */}
                    {FILE_CARRYING_FAMILIES.has(concept.type as TestedAssetType) && (
                    <div className="mt-3">
                      <span className="mb-1.5 block text-[11px] font-medium text-muted-foreground">
                        {t('questionnaire.addFileOptional')}
                      </span>
                      <div className="flex items-center gap-2">
                        {mediaUrl ? (
                          <div className="relative shrink-0">
                            {isVideoMedia(media) && mediaUrl ? (
                              <button
                                type="button"
                                className="relative h-11 w-14 cursor-zoom-in overflow-hidden rounded-[6px] border border-border"
                                title={media?.label || media?.altText || concept.name}
                                onClick={() => setVideoPreview({ src: mediaUrl, mimeType: media?.mimeType })}
                                data-testid={`questionnaire-concept-video-preview-${concept.id}`}
                              >
                                <CreativeMedia
                                  src={mediaUrl}
                                  mimeType={media?.mimeType}
                                  alt={concept.name}
                                  className="h-full w-full object-cover object-top"
                                />
                                <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                  <PlayFilled className="h-3.5 w-3.5 text-white drop-shadow" />
                                </span>
                              </button>
                            ) : previewUrl ? (
                              <button
                                type="button"
                                className="cursor-zoom-in"
                                onClick={() => setPreviewImage({
                                  src: hasImagePreview ? mediaUrl : previewUrl,
                                  alt: concept.name,
                                })}
                                data-testid={`questionnaire-concept-image-preview-${concept.id}`}
                              >
                                <img
                                  src={previewUrl}
                                  alt={concept.name}
                                  className="h-11 w-14 rounded-[6px] border border-border object-cover object-top transition-opacity hover:opacity-80"
                                />
                              </button>
                            ) : (
                              <div
                                className="flex h-11 w-14 items-center justify-center rounded-[6px] border border-border bg-background text-muted-foreground dark:bg-card"
                                title={media?.label || media?.altText || concept.name}
                                data-testid={`questionnaire-concept-document-preview-${concept.id}`}
                              >
                                <FileText className="h-4 w-4" />
                              </div>
                            )}
                            <button
                              type="button"
                              className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white shadow-sm transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => onUpdateConcept(concept.id, { media: concept.media.filter((m) => m.id !== media?.id && m.sliceOf !== media?.id) })}
                              disabled={disabled}
                              title={disabled ? disabledReason : undefined}
                              aria-label={t("questionnaire.removeFile")}
                              data-testid={`questionnaire-concept-image-remove-${concept.id}`}
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-background px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:text-foreground dark:bg-card disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => setCaptureConceptId(concept.id)}
                          disabled={disabled}
                          title={disabled ? disabledReason : undefined}
                          data-testid={`questionnaire-concept-url-capture-${concept.id}`}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          {t('questionnaire.addByUrl')}
                        </button>
                        <label
                          className={`inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-background px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors dark:bg-card ${
                            disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer hover:border-accent-highlight hover:text-foreground'
                          }`}
                          title={disabled ? disabledReason : undefined}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {isUploading
                            ? t('questionnaire.uploadingFile')
                            : mediaUrl
                              ? t('questionnaire.replaceFile')
                              : t('questionnaire.uploadFile')}
                          <input
                            type="file"
                            accept={ASSET_MEDIA_ACCEPT}
                            className="hidden"
                            disabled={isUploading || disabled}
                            data-testid={`questionnaire-concept-file-input-${concept.id}`}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const file = event.target.files?.[0]
                              event.currentTarget.value = ''
                              if (!file) return
                              setUploadingConceptId(concept.id)
                              // The workspace already reports upload failures with a toast.
                              void onUploadConceptImage(concept.id, file).catch(() => undefined).finally(() => {
                                setUploadingConceptId(null)
                              })
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    )}
                    {(EXPOSURE_BOUND_FAMILIES as ReadonlyArray<string>).includes(concept.type) ? (
                      // The marketer's word on this asset (C19B): the version
                      // they will run, or a stand-in for it. Choosing the
                      // selected one again makes it unknown, so Review asks.
                      <div
                        className="mt-2 flex flex-wrap items-center gap-2"
                        role="group"
                        aria-label={t('questionnaire.stimulusStatus.label')}
                        data-testid={`questionnaire-concept-stimulus-status-${concept.id}`}
                      >
                        <Text className="text-[12px] text-muted-foreground">
                          {t('questionnaire.stimulusStatus.label')}
                        </Text>
                        {(
                          [
                            ['final', 'questionnaire.stimulusStatus.final'],
                            ['stand_in', 'questionnaire.stimulusStatus.standIn'],
                          ] as const
                        ).map(([status, labelKey]) => {
                          const selected = concept.stimulusStatus === status
                          return (
                            <button
                              key={status}
                              type="button"
                              aria-pressed={selected}
                              disabled={disabled}
                              data-testid={`questionnaire-concept-stimulus-status-${concept.id}-${status}`}
                              onClick={() =>
                                onUpdateConcept(concept.id, {
                                  stimulusStatus: selected ? undefined : status,
                                })
                              }
                              className={cn(
                                'rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                selected
                                  ? 'border-accent-highlight bg-accent-highlight/10 text-foreground'
                                  : 'border-border bg-transparent text-muted-foreground hover:text-foreground',
                              )}
                            >
                              {t(labelKey)}
                            </button>
                          )
                        })}
                      </div>
                    ) : null}
                    {hasIssues ? (
                      <div className="mt-2 space-y-0.5 text-[11px] font-medium text-[#E5484D]">
                        {validationIssues.map((issue) => (
                          <div key={issue}>{issue}</div>
                        ))}
                      </div>
                    ) : null}
                    {advice.length > 0 ? (
                      <div
                        className="mt-2 space-y-1"
                        data-testid={`questionnaire-concept-server-issues-${concept.id}`}
                      >
                        {advice.map((issue) => (
                          <p
                            key={`${issue.code}:${issue.path}`}
                            className="rounded-[8px] border border-chart-warning/30 bg-chart-warning/10 px-2.5 py-1.5 text-[12px] leading-[1.45] text-foreground"
                          >
                            {formatIssueCopy(issue).text}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
            <button
              type="button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-[8px] border border-dashed border-border bg-transparent px-3 py-3 text-[13px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => addConceptExpanded()}
              disabled={addDisabled}
              title={addDisabledReason}
              data-testid="questionnaire-add-concept"
            >
              <Plus className="h-3.5 w-3.5" />
              Add {label.singular}
            </button>
            </div>
          </div>
        </div>
      </section>
    )}
    <UrlCaptureDialog
      capture={captureQuestionnaireUrlApi}
      notThePage={notThePageVerdict(
        concepts.find((concept) => concept.id === captureConceptId),
        captureConceptId ? (issuesByAssetId?.get(captureConceptId) ?? []) : [],
      )}
      open={captureConceptId !== null}
      onOpenChange={(open) => {
        if (!open) setCaptureConceptId(null)
      }}
      scale={1}
      onAcceptLink={
        onAttachConceptUrl &&
        savesCaptureOfLink(
          (concepts.find((concept) => concept.id === captureConceptId)?.type ?? 'concept') as TestedAssetType,
        )
          ? (url, line) => {
              if (!captureConceptId) return
              // The page stays its link, as the collect card keeps it; the Save
              // captures it, and the page's readiness line says what it needs.
              void onAttachConceptUrl(captureConceptId, url)
              toast(line)
              setCaptureConceptId(null)
            }
          : undefined
      }
      onAccept={(result, url) => {
        if (!captureConceptId) return
        // Direct video files attach as a URL (no screenshot). Player-page
        // hosts never reach accept — UrlCaptureDialog refuses them first.
        const videoUrl = result.source === 'video' ? result.publicUrl || '' : ''
        if (videoUrl) {
          if (!onAttachConceptUrl) {
            toast.error('Could not attach this video.')
            return
          }
          void onAttachConceptUrl(captureConceptId, videoUrl)
          return
        }
        // The backend stored the capture (bounded) and issued it; the link
        // it was taken from goes with it, as the collect card's does.
        // A capture the backend issued no file for is not kept: said, never
        // a silent no-op.
        if (result.file) onAttachConceptCapture(captureConceptId, result.file, url)
        else toast.error(refusedCaptureError('capture_returned_no_image', null))
      }}
    />
    </>
  )
}


